import { useEffect, useMemo, useState } from 'react';
import { Box, Container, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from 'layouts/dashboard/DashboardLayout';
import DevicesTable from 'pages/dashboard/sections/devices/DevicesTable';
import { useSelector, useDispatch } from 'react-redux';
import { fetchDevices } from 'store/actions/deviceActions';
import { Device } from 'store/reducers/deviceReducers';

const SEARCH_PARAM = 'search';

const matchesSearch = (device: Device, search: string) => {
  const query = search.toLowerCase();
  return (
    device.serial.toLowerCase().includes(query) ||
    device.target.name.toLowerCase().includes(query) ||
    device.group.name.toLowerCase().includes(query)
  );
};

const DeviceHome = () => {
  const dispatch = useDispatch();
  const { devices } = useSelector((state: any) => state.device);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchFromUrl = searchParams.get(SEARCH_PARAM) ?? '';
  const [searchInput, setSearchInput] = useState(searchFromUrl);

  useEffect(() => {
    dispatch({ type: fetchDevices.request });
  }, [dispatch]);

  useEffect(() => {
    setSearchInput(searchFromUrl);
  }, [searchFromUrl]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmedSearch = searchInput.trim();
      if (trimmedSearch === searchFromUrl) {
        return;
      }

      if (trimmedSearch) {
        setSearchParams({ [SEARCH_PARAM]: trimmedSearch });
      } else {
        setSearchParams({});
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput, searchFromUrl, setSearchParams]);

  const filteredDevices = useMemo(() => {
    const search = searchFromUrl.trim();
    if (!search) {
      return devices;
    }
    return devices.filter((device) => matchesSearch(device, search));
  }, [devices, searchFromUrl]);

  return (
    <DashboardLayout>
      <Box
        component='main'
        sx={{
          flexGrow: 1,
          py: 8,
        }}
      >
        <Container maxWidth='lg'>
          <Stack spacing={3}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={3}
              alignItems={{ xs: 'stretch', sm: 'center' }}
            >
              <Stack spacing={1} sx={{ flex: '1 1 auto' }}>
                <Typography variant='h4'>Devices</Typography>
              </Stack>
              <TextField
                size='small'
                variant='outlined'
                placeholder='Search by serial, target, or group...'
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                sx={{ width: { xs: '100%', sm: 360 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon fontSize='small' sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <DevicesTable rows={filteredDevices} />
          </Stack>
        </Container>
      </Box>
    </DashboardLayout>
  );
};

export default DeviceHome;
